// Feature: M005-F0013 (Teacher Classes page term-slot presentation)
// Tests: T-WEB-TERM-SENTENCE-001..006 (see termSlotWording.test.js in this directory)
// Design: studioaeye-Core docs/design/M005-D001-Term-booking-Management.md §10.5b
//
// Owner report on production, 2026-09-07, teacher of org 44 (user 312): "Also the
// class planer make no sence" — the old /app/teacher/classes term section was a
// seven-day grid where six of seven weekdays read "No term slots", and the one
// real card was an unlabelled stack of fragments ("19:30-22:00 / Jammer / DAH
// Studio 1 / 7 Sept 2026 - 18 Dec 2026 / accepted"). These helpers turn one
// assigned term slot booking into a plain-language sentence instead.
//
// Pure functions only: no React, no network, no Date.now() defaults baked in
// (callers pass real booking fields) so every branch here is deterministic and
// unit-testable in isolation.

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// termBooking.service.ts (studioaeye-Core) falls back to this literal string
// when a term instance has no real ti.name — treat it as "no name available",
// the same way the rest of this file treats a missing/empty term_name.
const GENERIC_TERM_NAME = 'term booking';

const STATUS_WORDING = {
  accepted: { label: 'Accepted', meaning: "you're teaching this slot" },
  // admin_confirmed reads to the teacher exactly like an accepted booking.
  admin_confirmed: { label: 'Accepted', meaning: "you're teaching this slot" },
  invited: { label: 'Invited', meaning: 'accept or reject below' },
  // prefilled is the same actionable pre-invitation state as invited.
  prefilled: { label: 'Invited', meaning: 'accept or reject below' },
  rejected: { label: 'Rejected', meaning: '' },
  cancelled: { label: 'Cancelled', meaning: 'by the studio' },
  expired: { label: 'Expired', meaning: '' },
};

const capitalize = (value) => (value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '');

export const getWeekdayLabel = (weekday) => {
  const index = Number(weekday);
  return WEEKDAY_LABELS[Number.isInteger(index) ? index : 0] || WEEKDAY_LABELS[0];
};

// Matches the existing formatTermTime in TeacherComponents.js: strip seconds,
// 'TBC' when unknown. Duplicated here (not imported) so this file stays a
// standalone, dependency-free util per the sibling-branch coordination note.
export const formatSlotTime = (value) => (value ? String(value).slice(0, 5) : 'TBC');

export const formatWeekdayTimeSentence = (weekday, startTime, endTime) => (
  `every ${getWeekdayLabel(weekday)} ${formatSlotTime(startTime)}-${formatSlotTime(endTime)}`
);

export const formatRoomPhrase = (roomName) => (
  roomName ? `in ${roomName}` : 'in a room to be confirmed'
);

// Fixed 3-letter abbreviations rather than toLocaleDateString(..., { month:
// 'short' }): the ICU data backing Intl varies by runtime (Node's ICU renders
// "Sept" for September; browsers typically render "Sep"), which would make
// this sentence render differently in a Jest run than in the deployed page.
const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateWords = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `${date.getUTCDate()} ${MONTH_ABBREVIATIONS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

// Inclusive week count between two date-only strings/Dates, e.g. 7 Sep 2026 -
// 18 Dec 2026 is 15 weeks (102 days apart -> floor(102/7)+1). Returns null
// when either date is missing/invalid or end precedes start.
export const computeWeekCount = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }
  const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) {
    return null;
  }
  return Math.floor(diffDays / 7) + 1;
};

// booking is the serializeTeacherBooking shape from GET /api/v1/me/term-bookings
// (termBooking.routes.ts, studioaeye-Core): start_date/end_date/term_name.
export const formatTermRangeSentence = (booking = {}) => {
  const start = booking.start_date || booking.term_start_date || booking.coverage_start_date || null;
  const end = booking.end_date || booking.term_end_date || booking.coverage_end_date || null;
  const rawName = typeof booking.term_name === 'string' ? booking.term_name.trim() : '';
  const hasName = Boolean(rawName) && rawName.toLowerCase() !== GENERIC_TERM_NAME;
  const namePrefix = hasName ? `${rawName}, ` : '';

  const startLabel = formatDateWords(start);
  if (!startLabel) {
    return hasName ? rawName : 'Dates to be confirmed';
  }

  const endLabel = formatDateWords(end);
  if (!endLabel) {
    // Open-ended term: no end date to compute a week count from.
    return `${namePrefix}from ${startLabel} (ongoing)`;
  }

  const weeks = computeWeekCount(start, end);
  const weeksSuffix = weeks ? ` (${weeks} week${weeks === 1 ? '' : 's'})` : '';
  return `${namePrefix}${startLabel} - ${endLabel}${weeksSuffix}`;
};

export const getTermSlotStatusWording = (status) => {
  const key = String(status || '').trim().toLowerCase();
  const known = STATUS_WORDING[key];
  if (known) {
    return { key, ...known };
  }
  return { key: key || 'unknown', label: capitalize(key) || 'Unknown', meaning: '' };
};

export const formatTermSlotStatusText = (status) => {
  const { label, meaning } = getTermSlotStatusWording(status);
  return meaning ? `${label} — ${meaning}` : label;
};
